/**
 * Landing on a shared Moon Exchange listing.
 *
 * `/play?listing=ID` is the link a seller sends. Whoever opens it may be a
 * signed-in player, the SELLER THEMSELVES, or a visitor with no account, and
 * the difference decides what they are offered -- Buy, Cancel, or a way to
 * create an account.
 *
 * The card is read from the PUBLIC endpoint rather than the authenticated
 * Convex client, because the visitor case has no client to read with, and
 * because the card is identical for every reader. Only the facts that depend on
 * WHO IS ASKING come from the authenticated query, so this door and the
 * exchange-row door end up drawing the same screen from the same two inputs.
 */

import {
  openListingDetail,
  updateListingDetail,
  currentListingId,
} from './exchange-listing-detail.js';

export function listingIdFromLocation(search) {
  try {
    const value = new URLSearchParams(search ?? window.location.search).get('listing');
    if (!value) return '';
    /* Same shape the server accepts. A Convex id is short and alphanumeric;
       anything else is not worth a round trip. */
    return /^[a-z0-9]{1,64}$/i.test(value) ? value : '';
  } catch (_) {
    return '';
  }
}

export async function fetchPublicListing(listingId, fetchImpl = fetch) {
  const res = await fetchImpl(`/api/exchange/listing?listing=${encodeURIComponent(listingId)}`);
  if (!res?.ok) return null;
  return res.json();
}

/**
 * THE CARD IN THE READER'S LANGUAGE (28 September 2026).
 *
 * The public listing carries its snapshot: the words stored, in English, when
 * the card was listed. The exchange row names a listing from the card itself,
 * in the reader's language, so this door reads that card too (the details
 * route; a visitor reads it without a session) and both doors print the same
 * words. Null when it cannot be read — a card deleted since it was listed.
 *
 * @param {string} cardId
 * @param {{ lang?: string, token?: string|null }} [options]
 */
export async function fetchListingCard(cardId, { lang = 'en', token = null } = {}, fetchImpl = fetch) {
  if (!cardId) return null;
  try {
    const params = new URLSearchParams({ cardIds: String(cardId), lang });
    const res = await fetchImpl(`/api/cards/details?${params.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res?.ok) return null;
    const cards = await res.json();
    return (Array.isArray(cards) ? cards : []).find((card) => card?.id === cardId) || null;
  } catch (_) {
    return null;
  }
}

/**
 * Is the app signed in RIGHT NOW, by its own reckoning?
 *
 * Not "has Firebase resolved". The listing view asked Firebase and was told
 * nothing: under the test harness's stubbed auth it does not deliver a user
 * either -- so a signed-in reader was offered "create an account to buy" on a
 * card they could have bought. `#user-controls` visible with a username in it
 * is the marker the rest of the product already treats as authoritative for
 * "signed in", and it is true however the session was established.
 */
export function appLooksSignedIn() {
  const controls = document.getElementById('user-controls');
  if (!controls) return false;
  if (controls.hidden || getComputedStyle(controls).display === 'none') return false;
  const name = document.getElementById('user-username');
  return Boolean(name && name.textContent && name.textContent.trim());
}

/**
 * The balance the player is already being shown — the EXACT one.
 *
 * Not the rendered text. `Stardust.format` is COMPACT above a thousand, so a
 * player with 2,000 sees "2K ☆", and stripping non-digits from that says they
 * have TWO. The listing then told them so and disabled a Buy button they could
 * easily afford. `data-count-value` is the unrounded number the top bar was
 * built from, written by both the animated and the plain paths, so this reads
 * what the bar means rather than what it printed.
 *
 * Undefined when there is nothing to read. Undefined is honest: it shows no
 * balance and blocks no purchase, where a wrong number does both.
 */
export function displayedStardust() {
  const raw = document.getElementById('user-stardust')?.dataset?.countValue;
  const numeric = Number(raw);
  return raw !== undefined && raw !== '' && Number.isFinite(numeric) ? numeric : undefined;
}

/**
 * IS THERE A SESSION ON THIS DEVICE? Answered in ~5ms, before the app boots.
 *
 * The guest path used to wait for `Firebase.subscribe` to fire with `null`,
 * which cannot happen until `Firebase.init()` runs — and that lives inside
 * `initializeApp()`, which itself waits for `convex-ready` or a THREE SECOND
 * fallback. So a visitor with no account sat on a placeholder for as long as
 * the app took to boot.
 *
 * Firebase persists its session in IndexedDB (`firebaseLocalStorageDb`, store
 * `firebaseLocalStorage`, one `firebase:authUser:…` key) — NOT localStorage,
 * which holds only Moonfall's own audio keys — and that is readable at document
 * start. `indexedDB.databases()` answers "does that database exist at all"
 * WITHOUT creating it, which matters because a plain `open()` on a fresh
 * browser fires `onupgradeneeded` and would have this module creating
 * Firebase's own storage.
 *
 * Fails safe in both directions: anything it cannot determine returns
 * `'unknown'` and the caller falls back to waiting for the real signal. It is
 * never used to conclude somebody IS signed in — only that nobody is.
 *
 * @returns {Promise<'none'|'session'|'unknown'>}
 */
export async function persistedSessionState() {
  try {
    if (typeof indexedDB === 'undefined') return 'unknown';
    if (typeof indexedDB.databases !== 'function') return 'unknown';
    const names = (await indexedDB.databases()).map((entry) => entry && entry.name);
    if (!names.includes('firebaseLocalStorageDb')) return 'none';
    return await new Promise((resolve) => {
      let settled = false;
      const finish = (value) => { if (!settled) { settled = true; resolve(value); } };
      /* A read that hangs must not hold the button hostage. */
      const deadline = setTimeout(() => finish('unknown'), 400);
      const done = (value) => { clearTimeout(deadline); finish(value); };
      const request = indexedDB.open('firebaseLocalStorageDb');
      request.onerror = () => done('unknown');
      /* The database vanished between the listing and the open. Abort so this
         module never CREATES Firebase's own storage as a side effect of asking
         whether it exists. `request.transaction`, not `event.target.transaction`
         — the event target is typed `EventTarget` and carries no transaction. */
      request.onupgradeneeded = () => {
        try { request.transaction?.abort(); } catch (_) { /* noop */ }
        done('none');
      };
      request.onsuccess = () => {
        const db = request.result;
        try {
          if (!db.objectStoreNames.contains('firebaseLocalStorage')) {
            db.close();
            done('none');
            return;
          }
          const keys = db.transaction('firebaseLocalStorage', 'readonly')
            .objectStore('firebaseLocalStorage').getAllKeys();
          keys.onerror = () => { db.close(); done('unknown'); };
          keys.onsuccess = () => {
            const hasUser = (keys.result || [])
              .some((key) => String(key).startsWith('firebase:authUser:'));
            db.close();
            done(hasUser ? 'session' : 'none');
          };
        } catch (_) {
          try { db.close(); } catch (__) { /* noop */ }
          done('unknown');
        }
      };
    });
  } catch (_) {
    return 'unknown';
  }
}

/**
 * Watch for the app becoming signed in, and keep watching.
 *
 * Two things have to be caught and they arrive at different times: a session
 * Firebase restores from disk (fast, usually under a second) and an account the
 * reader creates on this very screen because the listing asked them to (slow --
 * a Google popup, a consent screen, a username). The first version gave up
 * after 20 seconds, which is shorter than signing up takes, so the listing that
 * SENT them to the sign-in never came back afterwards.
 *
 * `onSettled(false)` fires once, when it becomes honest to draw "create an
 * account" -- and WHAT DECIDES THAT IS A SIGNAL, NOT A STOPWATCH.
 * `subscribeAuth` is the app's own Firebase subscription, which fires with
 * `null` for a reader who has no session and with a user for one who does; that
 * is the authoritative answer and it arrives when it arrives. A timer in its
 * place is a guess about how long a boot takes, and on a slow or contended
 * machine the guess expires first: measured, a signed-in reader was told to
 * create an account 3.5 seconds in, and the panel then swapped to Buy — the
 * exact retraction this screen exists to avoid.
 *
 * The timer survives only as a BACKSTOP for the case the signal never comes at
 * all (the Firebase SDK failed to load, so `init` returned false and nothing
 * ever fires). It is long, because being briefly offered nothing is a much
 * smaller error than being offered the wrong thing.
 *
 * @param {() => void} onSignedIn
 * @param {{ onSettled?: (signedIn: boolean) => void, settleMs?: number,
 *   intervalMs?: number, subscribeAuth?: (cb: (user: any) => void) => (() => void) }} [options]
 * @returns {() => void} stop watching
 */
export function watchSignIn(onSignedIn, {
  onSettled,
  settleMs = 12_000,
  intervalMs = 250,
  subscribeAuth,
} = {}) {
  let stopped = false;
  let announced = false;
  let guestAnnounced = false;
  /* Declared before anything can reach them: `goGuest` calls `slowPoll`, and a
     `const` arrow declared further down would be in its temporal dead zone if a
     subscription ever answered synchronously. */
  let timer = null;
  const slowPoll = () => {
    if (timer === null) return;
    clearInterval(timer);
    timer = setInterval(tick, Math.max(intervalMs, 1000));
  };

  /* Idempotent per transition, and it always re-reads the marker: the marker is
     the truth, so a settle that arrives while the reader is plainly signed in
     is simply wrong and is dropped. */
  const goGuest = () => {
    if (stopped || guestAnnounced || appLooksSignedIn()) return;
    guestAnnounced = true;
    /* SLOW DOWN once the question is answered. The poll still has to catch a
       reader who goes off and creates an account -- that is the whole reason it
       outlives the settle -- but four style recalculations a second, for the
       life of a page whose answer is already known, is not the way to wait for
       it. */
    slowPoll();
    try { onSettled?.(false); } catch (_) { /* never break the view */ }
  };

  const tick = () => {
    if (stopped) return;
    if (appLooksSignedIn()) {
      guestAnnounced = false;
      if (!announced) {
        announced = true;
        try { onSignedIn?.(); } catch (_) { /* never break the view */ }
      }
      return;
    }
    /* Signed OUT. The DOWNGRADE is not done here: a real sign-out fires
       `onAuthStateChanged(null)`, which reaches `goGuest` through the
       subscription above, and a second path to the same state was dead code
       that no test could falsify. This only rearms, so signing back in
       re-announces. */
    announced = false;
  };

  tick();
  timer = setInterval(tick, intervalMs);
  const deadline = setTimeout(goGuest, settleMs);
  let unsubscribe = () => {};
  if (typeof subscribeAuth === 'function') {
    try {
      unsubscribe = subscribeAuth((user) => {
        if (stopped) return;
        /* A user still has a profile read ahead of it before the app calls
           itself signed in; the poll above catches that. Only the definitive
           NO settles anything here. */
        if (!user) goGuest();
      }) || (() => {});
    } catch (_) { /* the backstop still covers it */ }
  }
  return () => {
    stopped = true;
    clearInterval(timer);
    clearTimeout(deadline);
    try { unsubscribe(); } catch (_) { /* noop */ }
  };
}

/**
 * The viewer-dependent half of the listing, for a signed-in reader.
 *
 * Returns null when it cannot be answered -- no cloud client yet, or the query
 * failed. Null means "still unknown", never "not yours": guessing `isMine`
 * false is exactly the bug this exists to fix.
 *
 * NO DEFAULT FOR `cloud`. It used to default to `window.MoonfallCloud`, which
 * the TEST BRIDGE publishes and the shipped bundle does not — so this resolved
 * a client under Playwright and `undefined` on a real page, and every signed-in
 * reader following a shared link would have been told "Could not check this
 * listing against your account". The caller owns the dependency and imports the
 * module; `clientHasNoBridgeOnlyGlobals` is the guard that says so.
 */
export async function fetchViewerFacts(listingId, cloud) {
  try {
    const result = await cloud?.moonExchange?.listingForViewer?.(listingId);
    if (!result || result.exists !== true) return null;
    return {
      isMine: result.isMine === true,
      unitPrice: Number(result.unitPrice) || 0,
      quantityRemaining: Number(result.quantityRemaining) || 0,
      available: result.available === true,
      /* The reader's own balance, from the row that owns it. Scraping the top
         bar instead meant reading a number that had not painted yet, and the
         link door then rendered a facts line with no balance while the row door
         showed one -- the two doors disagreeing about the one figure a buyer
         decides on. Left undefined when the server did not send it, which the
         view reads as "no line" rather than as zero. */
      stardustBalance: Number.isFinite(result.stardustBalance)
        ? Number(result.stardustBalance)
        : undefined,
      /* The Exchange unlock. Buying is gated as well as selling, so without
         this the panel offers a live Buy to a locked account and the press is
         answered with a server refusal. */
      exchangeUnlock: result.exchangeUnlock || null,
    };
  } catch (_) {
    return null;
  }
}

export const MoonfallExchangeDeepLink = {
  listingIdFromLocation,
  persistedSessionState,
  fetchPublicListing,
  fetchListingCard,
  appLooksSignedIn,
  displayedStardust,
  watchSignIn,
  fetchViewerFacts,
  openListingDetail,
  updateListingDetail,
  currentListingId,
};
